import type { CheckStatus, ProbeReport, PurposeDef } from "@/lib/api";
import { cn } from "@/lib/utils";
import { AlertTriangle, CheckCircle2, Loader2, MinusCircle, XCircle } from "lucide-react";
import { plainCheckNote, plainVerdictNote } from "@/lib/probe-copy";
import { VerdictBadge } from "./ModelParts";

// What a fitness test found: one row per check, then what that means for each
// kind of work. Nothing here blocks a choice; it tells people what to expect.

const CHECK_LABELS: Record<string, string> = {
  reachable: "Reachable",
  latency: "Speed",
  tool_calling: "Can use apps and tools",
  structured_output: "Fills in forms reliably",
  multi_turn_tools: "Long tasks with many steps",
  context_window: "Handles long documents",
  caching: "Prompt caching",
  cost: "Cost reporting",
  refusal_signal: "Refusal signals",
  anthropic_messages: "Works for writing code",
  embeddings: "Embeddings",
};

const CHECK_ICON: Record<CheckStatus, { Icon: typeof CheckCircle2; className: string; label: string }> = {
  pass: { Icon: CheckCircle2, className: "text-emerald-600 dark:text-emerald-400", label: "Passed" },
  warn: { Icon: AlertTriangle, className: "text-amber-600 dark:text-amber-400", label: "Warning" },
  fail: { Icon: XCircle, className: "text-rose-600 dark:text-rose-400", label: "Failed" },
  skipped: { Icon: MinusCircle, className: "text-muted-foreground", label: "Skipped" },
};

const EXPECTED_CHECKS = Object.keys(CHECK_LABELS).length;

export function FitnessReport({ report, purposeDefs }: { report: ProbeReport; purposeDefs?: PurposeDef[] }) {
  const running = report.status === "running";
  const labelOf = (id: string) => purposeDefs?.find((d) => d.id === id)?.label ?? id;
  return (
    <div className="space-y-5">
      <section className="space-y-2">
        <div className="flex items-center justify-between gap-2">
          <h4 className="text-sm font-semibold">Checks</h4>
          {running && (
            <span className="flex items-center gap-1.5 text-xs text-muted-foreground tabular-nums">
              <Loader2 className="size-3.5 animate-spin" />
              Checking, {report.checks.length} of about {EXPECTED_CHECKS} done
            </span>
          )}
        </div>
        <ul className="divide-y rounded-lg border">
          {report.checks.filter((check) => check.status !== "skipped").map((check) => {
            const icon = CHECK_ICON[check.status] ?? CHECK_ICON.skipped;
            return (
              <li key={check.id} className="flex gap-3 px-3 py-2.5">
                <icon.Icon className={cn("mt-0.5 size-4 shrink-0", icon.className)} aria-label={icon.label} />
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium leading-tight">{CHECK_LABELS[check.id] ?? check.id}</p>
                  {plainCheckNote(check.id, check.status, check.note) && (
                    <p className="text-xs text-muted-foreground">{plainCheckNote(check.id, check.status, check.note)}</p>
                  )}
                </div>
              </li>
            );
          })}
          {running && (
            <li className="flex items-center gap-3 px-3 py-2.5 text-sm text-muted-foreground">
              <Loader2 className="size-4 shrink-0 animate-spin" />
              Running the next check
            </li>
          )}
        </ul>
      </section>

      {!running && report.verdicts && report.verdicts.length > 0 && (
        <section className="space-y-2">
          <h4 className="text-sm font-semibold">What this means for each job</h4>
          <ul className="divide-y rounded-lg border">
            {report.verdicts.map((v) => {
              // Writing code runs on built-in models only, whatever the check found.
              const status = v.purpose === "coding" ? "unavailable" : v.status;
              const note = plainVerdictNote(v.purpose, v.note);
              return (
                <li key={v.purpose} className="flex flex-wrap items-start gap-x-3 gap-y-1 px-3 py-2.5">
                  <span className="w-48 shrink-0 text-sm font-medium">{labelOf(v.purpose)}</span>
                  <VerdictBadge status={status} />
                  {note && <p className="w-full text-xs text-muted-foreground sm:w-auto sm:flex-1">{note}</p>}
                </li>
              );
            })}
          </ul>
        </section>
      )}
    </div>
  );
}
